import { isAxiosError } from "axios";
import { NoNetworkError } from "@web-monorepo/fetchers";
import { DEFAULT_ERROR_MESSAGE, isNetworkFailure } from "#/src/utils/apiError";
import { logNetworkFailure } from "#/src/utils/networkFailureLog";
import { showNotification, type NotificationArgs } from "#/src/utils/notification";

export type NetworkFailureKind = "offline" | "timeout";

const NETWORK_FAILURE_NOTIFICATIONS: Record<NetworkFailureKind, NotificationArgs> = {
  offline: {
    title: "Server is down.",
    message: DEFAULT_ERROR_MESSAGE,
    color: "red",
    toastId: "network-failure-offline",
  },
  timeout: {
    title: "Network request aborted.",
    message: "An issue occurred while making a network request. Please try again in a few minutes.",
    color: "red",
    toastId: "network-failure-timeout",
  },
};

// The stable toastIds fold repeat failures (several requests dropping at once) into one toast.
export function notifyNetworkFailure(kind: NetworkFailureKind, notify: typeof showNotification = showNotification) {
  notify(NETWORK_FAILURE_NOTIFICATIONS[kind]);
}

// The fetchers counterpart of the axios errorHandler's network toast and log. Axios errors are
// skipped here because errorHandler already reported them. A raw useQuery/useMutation that renders
// its own retry state can set `meta: { silentNetworkError: true }` to skip the toast; like axios,
// the failure is still logged. Returns whether it reported anything.
export const reportFetcherNetworkFailure = (
  error: unknown,
  meta: Record<string, unknown> | undefined,
  notify: typeof showNotification = showNotification,
): boolean => {
  if (isAxiosError(error) || !isNetworkFailure(error)) return false;

  const tags = error instanceof NoNetworkError ? error.tags : undefined;
  // No errorMessage: NoNetworkError's message carries the full URL, query string included.
  logNetworkFailure({ method: tags?.method, requestPath: tags?.route, errorName: error.name });
  if (!meta?.silentNetworkError) notifyNetworkFailure("offline", notify);
  return true;
};
